import { Activity, Thermometer, Moon, Brain, Heart, Zap, Info } from 'lucide-react';

interface SymptomsStepProps {
  symptoms: Record<string, number>;
  updateFormData: (field: string, value: Record<string, number>) => void;
}

const SYMPTOM_CATEGORIES = {
  vasomotor: {
    title: 'Vasomotor Symptoms',
    icon: Thermometer,
    symptoms: [
      { id: 'hot_flashes', label: 'Hot flashes', icon: Thermometer },
      { id: 'night_sweats', label: 'Night sweats', icon: Moon },
      { id: 'cold_intolerance', label: 'Cold intolerance', icon: Thermometer },
    ]
  },
  neurological: {
    title: 'Mood & Cognitive',
    icon: Brain,
    symptoms: [
      { id: 'mood_swings', label: 'Mood swings', icon: Brain },
      { id: 'anxiety', label: 'Anxiety', icon: Brain },
      { id: 'depression', label: 'Depression', icon: Brain },
      { id: 'brain_fog', label: 'Brain fog / Memory issues', icon: Brain },
      { id: 'irritability', label: 'Irritability', icon: Brain },
    ]
  },
  physical: {
    title: 'Physical Symptoms',
    icon: Activity,
    symptoms: [
      { id: 'fatigue', label: 'Fatigue / Low energy', icon: Zap },
      { id: 'joint_pain', label: 'Joint pain', icon: Activity },
      { id: 'muscle_aches', label: 'Muscle aches', icon: Activity },
      { id: 'weight_gain', label: 'Weight gain / Difficulty losing weight', icon: Activity },
      { id: 'breast_tenderness', label: 'Breast tenderness', icon: Heart },
    ]
  },
  reproductive: {
    title: 'Reproductive Health',
    icon: Heart,
    symptoms: [
      { id: 'irregular_cycles', label: 'Irregular menstrual cycles', icon: Heart },
      { id: 'heavy_bleeding', label: 'Heavy menstrual bleeding', icon: Heart },
      { id: 'decreased_libido', label: 'Decreased libido', icon: Heart },
      { id: 'vaginal_dryness', label: 'Vaginal dryness', icon: Heart },
      { id: 'painful_intercourse', label: 'Painful intercourse', icon: Heart },
    ]
  },
  sleep: {
    title: 'Sleep Issues',
    icon: Moon,
    symptoms: [
      { id: 'insomnia', label: 'Difficulty falling asleep', icon: Moon },
      { id: 'frequent_waking', label: 'Frequent waking during night', icon: Moon },
      { id: 'unrefreshing_sleep', label: 'Unrefreshing sleep', icon: Moon },
    ]
  }
};

const SEVERITY_LABELS = [
  'None',
  'Mild',
  'Moderate', 
  'Severe',
  'Very Severe'
];

export default function SymptomsStep({ symptoms, updateFormData }: SymptomsStepProps) {
  const updateSymptom = (symptomId: string, severity: number) => {
    const newSymptoms = { ...symptoms };
    // Save all symptom interactions, including "None" (0)
    newSymptoms[symptomId] = severity;
    updateFormData('symptoms', newSymptoms);
  };

  return (
    <div className="space-y-8">
      {/* Custom slider styles */}
      <style>{`
        .symptom-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 3px;
          border-radius: 2px;
          cursor: pointer;
          outline: none;
        }
        .symptom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #2FFFC7;
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }
        .symptom-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #2FFFC7;
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }
      `}</style>
      {/* Header */}
      <div className="text-center space-y-3">
        <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#2FFFC7' }}>
          Step 3 of 7
        </p>
        <h2 className="text-3xl font-light tracking-tight" style={{ color: '#E6E9F0' }}>
          Current Symptoms
        </h2>
        <p className="text-lg" style={{ color: '#C1C9D2' }}>
          Rate the severity of any symptoms you're experiencing
        </p>
      </div>

      {/* Info Card */}
      <div 
        className="p-4 rounded-lg flex items-start gap-3"
        style={{
          background: 'rgba(47, 255, 199, 0.05)',
          border: '1px solid rgba(47, 255, 199, 0.2)',
        }}
      >
        <Info className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#2FFFC7' }} />
        <p className="text-sm" style={{ color: '#C1C9D2' }}>
          This information helps us understand your hormone health and create a personalized treatment plan. Select only the symptoms you currently experience.
        </p>
      </div>

      {/* Symptom Categories */}
      <div className="space-y-6">
        {Object.entries(SYMPTOM_CATEGORIES).map(([categoryKey, category]) => {
          const CategoryIcon = category.icon;
          const categoryHasSymptoms = category.symptoms.some(s => (symptoms[s.id] || 0) > 0);
          
          return (
            <div 
              key={categoryKey}
              className="p-6 rounded-xl"
              style={{
                background: categoryHasSymptoms 
                  ? 'rgba(47, 255, 199, 0.05)' 
                  : 'rgba(255, 255, 255, 0.02)',
                border: categoryHasSymptoms 
                  ? '1px solid rgba(47, 255, 199, 0.2)' 
                  : '1px solid rgba(255, 255, 255, 0.05)',
              }}
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <CategoryIcon className="h-5 w-5" style={{ color: '#2FFFC7' }} />
                <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
                  {category.title}
                </h3>
              </div>

              {/* Symptoms in this category */}
              <div className="space-y-4">
                {category.symptoms.map((symptom) => {
                  const SymptomIcon = symptom.icon;
                  const severity = symptoms[symptom.id] || 0;
                  
                  return (
                    <div key={symptom.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <SymptomIcon 
                            className="h-4 w-4" 
                            style={{ color: severity > 0 ? '#2FFFC7' : '#9DA9B8' }} 
                          />
                          <label 
                            className="text-sm font-medium"
                            style={{ color: severity > 0 ? '#E6E9F0' : '#C1C9D2' }}
                          >
                            {symptom.label}
                          </label>
                        </div>
                        <span 
                          className="text-xs font-medium px-2 py-1 rounded"
                          style={{
                            color: severity > 0 ? '#2FFFC7' : '#9DA9B8',
                            background: severity > 0 ? 'rgba(47, 255, 199, 0.1)' : 'transparent',
                          }}
                        >
                          {SEVERITY_LABELS[severity]}
                        </span>
                      </div>
                      
                      {/* Severity Slider */}
                      <input
                        type="range"
                        min="0"
                        max="4"
                        value={severity}
                        onChange={(e) => updateSymptom(symptom.id, parseInt(e.target.value))}
                        className="symptom-slider w-full"
                        style={{
                          background: `linear-gradient(to right, #2FFFC7 0%, #2FFFC7 ${severity * 25}%, rgba(255, 255, 255, 0.15) ${severity * 25}%, rgba(255, 255, 255, 0.15) 100%)`,
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {Object.keys(symptoms).length > 0 && (
        <div 
          className="p-4 rounded-lg"
          style={{
            background: 'rgba(47, 255, 199, 0.05)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
          }}
        >
          <p className="text-sm font-medium mb-2" style={{ color: '#2FFFC7' }}>
            Summary: {Object.keys(symptoms).length} symptom(s) reported
          </p>
          <p className="text-xs" style={{ color: '#C1C9D2' }}>
            This information will help your provider create a targeted treatment plan.
          </p>
        </div>
      )}
    </div>
  );
}

export { SYMPTOM_CATEGORIES, SEVERITY_LABELS };
